import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { SearchLoadingSectionLine } from '#/modules/search/components/Loading/SearchLoadingSectionLine';
import {
  useIsVolopSearchDeduplicateEnabled,
  useOrgVolopCountsByOrgId,
} from '#/modules/search/contexts/SearchContext';
import type { SearchType } from '#/modules/search/types/SearchType';
import { LISTING_TYPE_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypeHumanNameMap';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypePluralHumanNameMap';

type Props = {
  count: number | null;
  searchType: SearchType;
  tag?: 'h2' | 'div';
};

const formatNumber = (num: number) =>
  new Intl.NumberFormat('en-US').format(num);

export function SearchResultsHeaderCount({
  count,
  searchType,
  tag = 'h2',
}: Props) {
  const { i18n } = useI18n();
  const isVolopSearchDeduplicateEnabled = useIsVolopSearchDeduplicateEnabled();
  const orgVolopCounts = useOrgVolopCountsByOrgId();

  const isDedupedVolopSearch =
    isVolopSearchDeduplicateEnabled && searchType === 'VOLOP';

  // Under dedupe Algolia's nbHits reflects distinct-org count (via `distinct: 1`),
  // so recover the listing total by summing the parallel facet query results.
  // Bounded by `maxValuesPerFacet: 1000` in SearchContext — undercounts when more
  // than 1000 orgs match. See TODO at SearchContext.tsx:591.
  const dedupedListingTotal =
    orgVolopCounts === undefined
      ? null
      : Object.values(orgVolopCounts).reduce((sum, n) => sum + n, 0);

  const effectiveCount = isDedupedVolopSearch ? dedupedListingTotal : count;

  if (effectiveCount === null)
    return <SearchLoadingSectionLine width={64} height={20} />;

  const text =
    effectiveCount === 1
      ? i18n('1 %(searchType)s', {
          searchType: LISTING_TYPE_HUMAN_NAME_MAP(i18n)[searchType],
        })
      : i18n('%(count)s %(searchType)s', {
          count: formatNumber(effectiveCount),
          searchType: LISTING_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[searchType],
        });

  return (
    <Text
      tag={tag}
      styleAs="body"
      display={['block', 'block', 'block', 'inline-block']}
      fontSize="16px!important"
      mb={0}
      qaId="result-count"
      data-result-count={effectiveCount}
    >
      {text}
    </Text>
  );
}
